import React, { useCallback, useEffect, useState } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
  TablePagination,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Divider,
  Typography,
  Grid,
  LinearProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { ButtonBase } from "@mui/material";
import { IconEdit, IconTrash, IconPlus, IconChartBar } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import MainCard from "ui-component/cards/MainCard";
import SearchSection from "layout/MainLayout/Header/SearchSection";
import useViaCep from "hooks/useViaCep";
import useCnpj from "hooks/useCnpj";

const EditButton = styled(ButtonBase)(({ theme }) => ({
  color: theme.palette.secondary.dark,
  "&:hover": {
    background: theme.palette.secondary.dark,
    color: theme.palette.secondary.light,
  },
  width: 40,
  height: 40,
  borderRadius: 10,
  cursor: "pointer",
}));

const DeleteButton = styled(ButtonBase)(({ theme }) => ({
  color: theme.palette.error.dark,
  "&:hover": {
    background: theme.palette.error.dark,
    color: theme.palette.error.light,
  },
  width: 40,
  height: 40,
  borderRadius: 12,
  cursor: "pointer",
}));

const DataButton = styled(ButtonBase)(({ theme }) => ({
  color: theme.palette.secondary.dark,
  "&:hover": {
    background: theme.palette.secondary.dark,
    color: theme.palette.secondary.light,
  },
  width: 40,
  height: 40,
  borderRadius: 10,
  cursor: "pointer",
}));

const AddButton = styled(ButtonBase)(({ theme }) => ({
  color: theme.palette.info.dark,
  "&:hover": {
    background: theme.palette.secondary.dark,
    color: theme.palette.secondary.light,
  },
  align: "center",
  gap: 8,
  width: 150,
  height: 40,
  borderRadius: 10,
  display: "flex",
  cursor: "pointer",
}));

const formatCNPJ = (cnpj) => {
  cnpj = cnpj.replace(/\D/g, ""); // Remove caracteres não numéricos
  return cnpj.replace(
    /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
    "$1.$2.$3/$4-$5"
  );
};

const BanksList = () => {
  const navigate = useNavigate();
  const { fetchAddress, loading: cepLoading, error: cepError } = useViaCep();
  const { fetchCompanyData, loading: cnpjLoading, error: cnpjError } = useCnpj();
  const [banks, setBanks] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [page, setPage] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [loadingBank, setLoadingBank] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentBank, setCurrentBank] = useState({
    code: "",
    name: "",
    cnpj: "",
    mail: "",
    address_street: "",
    address_number: "",
    address_compl: "",
    address_city: "",
    address_state: "",
    address_neighborhood: "",
    address_zipcode: "",
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const fetchBanks = useCallback(() => {
    const searchText = searchTerm !== "" ? `&contains=${searchTerm}` : "";
    fetch(
      `/backend/banks/?page=${page + 1}&page_size=${rowsPerPage}${searchText}`
    )
      .then(async (response) => {
        if (!response.ok) {
          let errorMessage = "Falha ao carregar os bancos";
          try {
            const errorData = await response.json();
            if (errorData.error) {
              errorMessage = errorData.error;
            } else if (errorData.detail) {
              errorMessage = errorData.detail;
            }
          } catch (e) {
            console.warn("Não foi possível extrair mensagem de erro específica:", e);
          }
          throw new Error(errorMessage);
        }
        return response.json();
      })
      .then((data) => {
        setBanks(data.results);
        setTotalRecords(data.count);
      })
      .catch((error) => {
        console.error("Erro ao carregar bancos:", error);
        alert(error.message);
      });
  }, [page, rowsPerPage, searchTerm]);

  useEffect(() => {
    fetchBanks();
  }, [fetchBanks, page, rowsPerPage, searchTerm]);

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleEdit = async (bank) => {
    setFieldErrors({});
    setIsEditing(true);
    setLoadingBank(true);
    setOpenModal(true);
    try {
      const response = await fetch(`/backend/banks/${bank.id}/`);
      if (!response.ok) throw new Error("Falha ao carregar o banco");
      const fullBank = await response.json();
      const bankWithFormattedFields = {
        ...fullBank,
        mail: fullBank.mail || "",
        address_street: fullBank.address_street || "",
        address_number: fullBank.address_number || "",
        address_compl: fullBank.address_compl || "",
        address_city: fullBank.address_city || "",
        address_state: fullBank.address_state || "",
        address_neighborhood: fullBank.address_neighborhood || "",
        address_zipcode: formatZipCode(fullBank.address_zipcode || "")
      };
      setCurrentBank(bankWithFormattedFields);
    } catch (error) {
      console.error("Erro ao carregar banco:", error);
    } finally {
      setLoadingBank(false);
    }
  };

  const handleAdd = () => {
    setCurrentBank({
      code: "",
      name: "",
      cnpj: "",
      mail: "",
      address_street: "",
      address_number: "",
      address_compl: "",
      address_city: "",
      address_state: "",
      address_neighborhood: "",
      address_zipcode: "",
    });
    setFieldErrors({});
    setIsEditing(false);
    setOpenModal(true);
  };

  const handleCloseModal = () => setOpenModal(false);

  const handleCloseWithConfirmation = () => {
    if (window.confirm("Você tem certeza que deseja fechar? Os dados não salvos serão perdidos.")) {
      setOpenModal(false);
    }
  };

  // Função para formatar o CEP
  const formatZipCode = (zipcode) => {
    if (!zipcode) return "";
    
    // Remove todos os caracteres não numéricos
    const rawValue = zipcode.replace(/\D/g, "");
    
    // Aplica a formatação brasileira de CEP
    if (rawValue.length >= 6) {
      // Formato: 12345-678
      return `${rawValue.slice(0, 5)}-${rawValue.slice(5, 8)}`;
    }
    
    return rawValue;
  };

  // Função para extrair apenas os dígitos do CEP
  const extractZipCodeDigits = (zipcode) => {
    return zipcode ? zipcode.replace(/\D/g, "") : "";
  };

  // Função para buscar endereço pelo CEP
  const handleCepBlur = async (cep) => {
    const cleanCep = extractZipCodeDigits(cep);
    
    // Só busca se o CEP tiver 8 dígitos
    if (cleanCep.length === 8) {
      const addressData = await fetchAddress(cleanCep);
      
      if (addressData) {
        setCurrentBank(prevBank => ({
          ...prevBank,
          // Só preenche campos que estiverem em branco
          address_street: !prevBank.address_street && addressData.street ? addressData.street : prevBank.address_street,
          address_neighborhood: !prevBank.address_neighborhood && addressData.neighborhood ? addressData.neighborhood : prevBank.address_neighborhood,
          address_city: !prevBank.address_city && addressData.city ? addressData.city : prevBank.address_city,
          address_state: !prevBank.address_state && addressData.state ? addressData.state : prevBank.address_state,
          address_compl: !prevBank.address_compl && addressData.complement ? addressData.complement : prevBank.address_compl,
        }));
      }
    }
  };

  // Função para buscar dados da empresa pelo CNPJ
  const handleCnpjBlur = async (cnpj) => {
    const cleanCnpj = cnpj.replace(/\D/g, '');
    
    // Só busca se o CNPJ tiver 14 dígitos
    if (cleanCnpj.length === 14) {
      console.log('🏦 [BanksList] Buscando dados para CNPJ:', cleanCnpj);
      const companyData = await fetchCompanyData(cleanCnpj);
      
      if (companyData) {
        console.log('🏦 [BanksList] Preenchendo formulário com:', companyData.name || 'dados obtidos');
        
        setCurrentBank(prevBank => ({
          ...prevBank,
          // Preenche o nome do banco se estiver em branco
          name: !prevBank.name && companyData.name ? companyData.name : prevBank.name,
          mail: !prevBank.mail && companyData.email ? companyData.email : prevBank.mail,
          // Só preenche campos de endereço que estiverem em branco
          address_street: !prevBank.address_street && companyData.street ? companyData.street : prevBank.address_street,
          address_number: !prevBank.address_number && companyData.number ? companyData.number : prevBank.address_number,
          address_compl: !prevBank.address_compl && companyData.complement ? companyData.complement : prevBank.address_compl,
          address_neighborhood: !prevBank.address_neighborhood && companyData.neighborhood ? companyData.neighborhood : prevBank.address_neighborhood,
          address_city: !prevBank.address_city && companyData.city ? companyData.city : prevBank.address_city,
          address_state: !prevBank.address_state && companyData.state ? companyData.state : prevBank.address_state,
          address_zipcode: !prevBank.address_zipcode && companyData.zipcode ? formatZipCode(companyData.zipcode) : prevBank.address_zipcode,
        }));
      }
    }
  };

  const handleSave = () => {
    // Limpa erros anteriores
    setFieldErrors({});
    
    // Validação de campos obrigatórios no frontend
    const errors = {};
    if (!currentBank.code || !currentBank.code.trim()) {
      errors.code = "O código é obrigatório";
    }
    if (!currentBank.name || !currentBank.name.trim()) {
      errors.name = "O nome é obrigatório";
    }
    
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    const method = isEditing ? "PUT" : "POST";
    const url = isEditing
      ? `/backend/banks/${currentBank.id}/`
      : "/backend/banks/";

    // Prepara os dados para envio, removendo a máscara do CEP
    const bankDataToSave = {
      ...currentBank,
      address_zipcode: extractZipCodeDigits(currentBank.address_zipcode)
    };

    fetch(url, {
      method: method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(bankDataToSave),
    })
      .then(async (response) => {
        if (response.ok) return response.json();
        
        // Tenta extrair os erros específicos de cada campo do backend
        const errors = {};
        let generalError = null;
        
        try {
          const errorData = await response.json();
          
          // Mapeia os erros do backend para os campos do formulário
          const fieldMapping = {
            code: 'code',
            name: 'name',
            cnpj: 'cnpj',
            mail: 'mail',
            address_street: 'address_street',
            address_number: 'address_number',
            address_compl: 'address_compl',
            address_neighborhood: 'address_neighborhood',
            address_city: 'address_city',
            address_state: 'address_state',
            address_zipcode: 'address_zipcode'
          };
          
          // Processa erros de campos específicos
          Object.keys(fieldMapping).forEach(backendField => {
            if (errorData[backendField] && Array.isArray(errorData[backendField])) {
              errors[fieldMapping[backendField]] = errorData[backendField][0];
            }
          });
          
          // Erros gerais
          if (errorData.error) {
            generalError = errorData.error;
          } else if (errorData.non_field_errors && errorData.non_field_errors[0]) {
            generalError = errorData.non_field_errors[0];
          } else if (errorData.detail) {
            generalError = errorData.detail;
          }
          
          // Se houver erros de campos, atualiza o estado
          if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
          }
          
          // Monta mensagem de erro geral
          if (generalError) {
            throw new Error(generalError);
          } else if (Object.keys(errors).length > 0) {
            // Se houver erros de campos, cria uma mensagem resumida
            const errorFields = Object.keys(errors);
            throw new Error(`Erro de validação nos campos: ${errorFields.join(', ')}. Verifique os campos destacados.`);
          } else {
            throw new Error("Erro ao salvar banco. Verifique os dados informados.");
          }
        } catch (e) {
          if (e instanceof Error && e.message) {
            throw e;
          }
          console.warn("Não foi possível extrair mensagem de erro específica:", e);
          throw new Error("Erro ao salvar banco. Tente novamente.");
        }
      })
      .then((data) => {
        // Aplica a formatação do CEP nos dados retornados
        const dataWithFormattedFields = {
          ...data,
          address_zipcode: formatZipCode(data.address_zipcode || "")
        };
        
        if (isEditing) {
          setBanks((prevBanks) =>
            prevBanks.map((bank) => (bank.id === data.id ? data : bank))
          );
          // Atualiza o currentBank com os campos formatados
          setCurrentBank(dataWithFormattedFields);
        } else {
          setBanks((prevBanks) => [...prevBanks, data]);
        }
        setFieldErrors({});
        alert("Banco salvo com sucesso!");
        setOpenModal(false);
        fetchBanks();
      })
      .catch((error) => {
        console.error("Erro ao salvar banco:", error);
        // Se não houver erros de campos específicos, mostra alerta
        if (Object.keys(fieldErrors).length === 0) {
          alert(error.message);
        }
      });
  };

  const handleDelete = (bankId) => {
    if (window.confirm("Você tem certeza que deseja excluir este banco?")) {
      fetch(`/backend/banks/${bankId}/`, {
        method: "DELETE",
      })
        .then(async (response) => {
          if (response.ok) {
            setBanks((prevBanks) =>
              prevBanks.filter((bank) => bank.id !== bankId)
            );
            alert("Banco excluído com sucesso!");
            fetchBanks();
          } else {
            // Tenta extrair a mensagem de erro específica do backend
            let errorMessage = "Erro ao excluir banco";
            try {
              const errorData = await response.json();
              if (errorData.error) {
                errorMessage = errorData.error;
              } else if (errorData.detail) {
                errorMessage = errorData.detail;
              }
            } catch (e) {
              // Se não conseguir parsear o JSON, usa a mensagem padrão
              console.warn("Não foi possível extrair mensagem de erro específica:", e);
            }
            throw new Error(errorMessage);
          }
        })
        .catch((error) => {
          console.error("Erro ao excluir banco:", error);
          alert(error.message);
        });
    }
  };

  const handleViewAnnualData = (bankId) => {
    navigate(`/banks/annual-data/list?bank=${bankId}`);
  };

  return (
    <div className="w-full overflow-auto">
      <MainCard
        title="Cadastro de bancos"
        actions={[
          <AddButton onClick={handleAdd}>
            <IconPlus stroke={1.5} size="1.1rem" sx={{ mr: 5 }} />
            Adicionar banco
          </AddButton>,
        ]}
      >
        <SearchSection onSearchChange={(value) => { setSearchTerm(value); setPage(0); }} />
        <Divider sx={{ my: 2 }} />
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Código</TableCell>
              <TableCell>Nome</TableCell>
              <TableCell>CNPJ</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {banks.map((bank) => (
              <TableRow key={bank.id}>
                <TableCell>{bank.code}</TableCell>
                <TableCell>{bank.name}</TableCell>
                <TableCell>{formatCNPJ(bank.cnpj)}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Dados Anuais">
                    <DataButton onClick={() => handleViewAnnualData(bank.id)}>
                      <IconChartBar stroke={1.5} size="1.3rem" />
                    </DataButton>
                  </Tooltip>
                  <Tooltip title="Editar">
                    <EditButton onClick={() => handleEdit(bank)}>
                      <IconEdit stroke={1.5} size="1.3rem" />
                    </EditButton>
                  </Tooltip>
                  <Tooltip title="Excluir">
                    <DeleteButton onClick={() => handleDelete(bank.id)}>
                      <IconTrash stroke={1.5} size="1.3rem" />
                    </DeleteButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </MainCard>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50, 100, 150]}
        component="div"
        count={totalRecords}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Bancos por página"
      />

      <Dialog 
        open={openModal} 
        onClose={(event, reason) => {
          // Impede o fechamento do modal ao clicar fora
          if (reason === 'backdropClick') {
            return;
          }
          // Para tecla ESC, solicita confirmação
          if (reason === 'escapeKeyDown') {
            handleCloseWithConfirmation();
            return;
          }
          handleCloseModal();
        }}
      >
        <DialogTitle>
          {isEditing ? "Editar Banco" : "Adicionar Banco"}
        </DialogTitle>
        {loadingBank && <LinearProgress />}
        <DialogContent>
          <Grid container spacing={2}>
            {/* Código */}
            <Grid item xs={4}>
              <TextField
                label="Código"
                value={currentBank.code || ""}
                onChange={(e) => {
                  setCurrentBank({ ...currentBank, code: e.target.value });
                  // Remove erro do campo quando o usuário começar a digitar
                  if (fieldErrors.code) {
                    setFieldErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.code;
                      return newErrors;
                    });
                  }
                }}
                fullWidth
                required
                inputProps={{ maxLength: 3 }}
                sx={{ mt: 2 }}
                autoComplete="new-password"
                error={!!fieldErrors.code}
                helperText={fieldErrors.code || "Máximo 3 caracteres"}
              />
            </Grid>

            {/* Nome */}
            <Grid item xs={8}>
              <TextField
                label="Nome"
                value={currentBank.name || ""}
                onChange={(e) => {
                  setCurrentBank({ ...currentBank, name: e.target.value });
                  // Remove erro do campo quando o usuário começar a digitar
                  if (fieldErrors.name) {
                    setFieldErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.name;
                      return newErrors;
                    });
                  }
                }}
                fullWidth
                required
                inputProps={{ maxLength: 100 }}
                sx={{ mt: 2 }}
                autoComplete="new-password"
                error={!!fieldErrors.name}
                helperText={fieldErrors.name || "Máximo 100 caracteres"}
              />
            </Grid>

            {/* CNPJ */}
            <Grid item xs={12}>
              <TextField
                label="CNPJ"
                value={formatCNPJ(currentBank.cnpj || "")}
                onChange={(e) => {
                  const rawValue = e.target.value
                    .replace(/\D/g, "")
                    .slice(0, 14);
                  setCurrentBank({ ...currentBank, cnpj: rawValue });
                  // Remove erro do campo quando o usuário começar a digitar
                  if (fieldErrors.cnpj) {
                    setFieldErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.cnpj;
                      return newErrors;
                    });
                  }
                }}
                onBlur={(e) => handleCnpjBlur(e.target.value)}
                fullWidth
                inputProps={{ maxLength: 18 }} // 18 para incluir formatação: 00.000.000/0000-00
                autoComplete="new-password"
                disabled={cnpjLoading}
                error={!!fieldErrors.cnpj || !!cnpjError}
                helperText={
                  fieldErrors.cnpj 
                    ? fieldErrors.cnpj
                    : cnpjLoading 
                      ? "Buscando dados da empresa..." 
                      : cnpjError 
                        ? `Erro: ${cnpjError}` 
                        : "Digite o CNPJ para buscar os dados automaticamente (14 dígitos)"
                }
              />
            </Grid>

            {/* E-mail */}
            <Grid item xs={12}>
              <TextField
                label="E-mail"
                type="email"
                value={currentBank.mail || ""}
                onChange={(e) => {
                  const value = e.target.value.slice(0, 60);
                  setCurrentBank({ ...currentBank, mail: value });
                  if (fieldErrors.mail) {
                    setFieldErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.mail;
                      return newErrors;
                    });
                  }
                }}
                fullWidth
                inputProps={{ maxLength: 60 }}
                autoComplete="new-password"
                error={!!fieldErrors.mail}
                helperText={fieldErrors.mail || "Máximo 60 caracteres"}
              />
            </Grid>
          </Grid>

          {/* Subtítulo Endereço */}
          <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
            Endereço
          </Typography>

          <Grid container spacing={2}>
            {/* CEP - Primeiro campo para buscar endereço */}
            <Grid item xs={4}>
              <TextField
                label="CEP"
                value={currentBank.address_zipcode || ""}
                onChange={(e) => {
                  // Remove todos os caracteres não numéricos e limita a 8 dígitos
                  const rawValue = e.target.value.replace(/\D/g, "").slice(0, 8);
                  
                  // Aplica a formatação usando a função formatZipCode
                  const formattedValue = formatZipCode(rawValue);
                  
                  setCurrentBank({
                    ...currentBank,
                    address_zipcode: formattedValue,
                  });
                  if (fieldErrors.address_zipcode) {
                    setFieldErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.address_zipcode;
                      return newErrors;
                    });
                  }
                }}
                onBlur={(e) => handleCepBlur(e.target.value)}
                fullWidth
                variant="outlined"
                inputProps={{ maxLength: 9 }} // 12345-678
                placeholder="12345-678"
                autoComplete="new-password"
                disabled={cepLoading}
                error={!!fieldErrors.address_zipcode || !!cepError}
                helperText={
                  fieldErrors.address_zipcode
                    ? fieldErrors.address_zipcode
                    : cepLoading 
                      ? "Buscando endereço..." 
                      : cepError 
                        ? `Erro: ${cepError}` 
                        : "Digite o CEP para buscar o endereço automaticamente"
                }
              />
            </Grid>

            {/* Rua */}
            <Grid item xs={6}>
              <TextField
                label="Rua"
                value={currentBank.address_street || ""}
                onChange={(e) => {
                  const value = e.target.value.slice(0, 100);
                  setCurrentBank({
                    ...currentBank,
                    address_street: value,
                  });
                  if (fieldErrors.address_street) {
                    setFieldErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.address_street;
                      return newErrors;
                    });
                  }
                }}
                fullWidth
                variant="outlined"
                inputProps={{ maxLength: 100 }}
                autoComplete="new-password"
                error={!!fieldErrors.address_street}
                helperText={fieldErrors.address_street || "Máximo 100 caracteres"}
              />
            </Grid>

            {/* Número */}
            <Grid item xs={2}>
              <TextField
                label="Nº"
                value={currentBank.address_number || ""}
                onChange={(e) => {
                  const value = e.target.value.slice(0, 20);
                  setCurrentBank({
                    ...currentBank,
                    address_number: value,
                  });
                  if (fieldErrors.address_number) {
                    setFieldErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.address_number;
                      return newErrors;
                    });
                  }
                }}
                fullWidth
                variant="outlined"
                inputProps={{ maxLength: 20 }}
                autoComplete="new-password"
                error={!!fieldErrors.address_number}
                helperText={fieldErrors.address_number || "Máximo 20 caracteres"}
              />
            </Grid>

            {/* Bairro */}
            <Grid item xs={6}>
              <TextField
                label="Bairro"
                value={currentBank.address_neighborhood || ""}
                onChange={(e) => {
                  const value = e.target.value.slice(0, 60);
                  setCurrentBank({
                    ...currentBank,
                    address_neighborhood: value,
                  });
                  if (fieldErrors.address_neighborhood) {
                    setFieldErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.address_neighborhood;
                      return newErrors;
                    });
                  }
                }}
                fullWidth
                variant="outlined"
                inputProps={{ maxLength: 60 }}
                autoComplete="new-password"
                error={!!fieldErrors.address_neighborhood}
                helperText={fieldErrors.address_neighborhood || "Máximo 60 caracteres"}
              />
            </Grid>

            {/* Complemento */}
            <Grid item xs={6}>
              <TextField
                label="Complemento"
                value={currentBank.address_compl || ""}
                onChange={(e) => {
                  const value = e.target.value.slice(0, 50);
                  setCurrentBank({
                    ...currentBank,
                    address_compl: value,
                  });
                  if (fieldErrors.address_compl) {
                    setFieldErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.address_compl;
                      return newErrors;
                    });
                  }
                }}
                fullWidth
                variant="outlined"
                inputProps={{ maxLength: 50 }}
                autoComplete="new-password"
                error={!!fieldErrors.address_compl}
                helperText={
                  fieldErrors.address_compl 
                    ? fieldErrors.address_compl 
                    : `${(currentBank.address_compl || "").length}/50 caracteres`
                }
              />
            </Grid>

            {/* Cidade */}
            <Grid item xs={4}>
              <TextField
                label="Cidade"
                value={currentBank.address_city || ""}
                onChange={(e) => {
                  const value = e.target.value.slice(0, 60);
                  setCurrentBank({
                    ...currentBank,
                    address_city: value,
                  });
                  if (fieldErrors.address_city) {
                    setFieldErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.address_city;
                      return newErrors;
                    });
                  }
                }}
                fullWidth
                variant="outlined"
                inputProps={{ maxLength: 60 }}
                autoComplete="new-password"
                error={!!fieldErrors.address_city}
                helperText={fieldErrors.address_city || "Máximo 60 caracteres"}
              />
            </Grid>

            {/* UF */}
            <Grid item xs={2}>
              <TextField
                label="UF"
                value={currentBank.address_state || ""}
                onChange={(e) => {
                  const value = e.target.value.toUpperCase().slice(0, 2);
                  setCurrentBank({
                    ...currentBank,
                    address_state: value,
                  });
                  if (fieldErrors.address_state) {
                    setFieldErrors(prev => {
                      const newErrors = { ...prev };
                      delete newErrors.address_state;
                      return newErrors;
                    });
                  }
                }}
                fullWidth
                variant="outlined"
                inputProps={{ maxLength: 2 }}
                autoComplete="new-password"
                error={!!fieldErrors.address_state}
                helperText={fieldErrors.address_state || "Ex: SP, RJ, MG"}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseModal} color="secondary">
            Cancelar
          </Button>
          <Button onClick={handleSave} color="primary">
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default BanksList;
